import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, Quote, CheckCircle2 } from 'lucide-react';
import { fadeInUp, staggerContainer, viewportConfig } from '../../utils/animationVariants';

// --- Types ---

export interface Testimonial {
    quote: string;
    author: string;
    role: string;
    avatar?: string;
}

export interface FAQ {
    question: string;
    answer: string;
}

export interface Step {
    title: string;
    description: string;
    icon: React.ReactNode;
}

// --- Testimonial Section ---

export const TestimonialSection: React.FC<{ testimonials: Testimonial[] }> = ({ testimonials }) => {
    return (
        <section className="py-24 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportConfig}
                    variants={staggerContainer}
                    className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                    {testimonials.map((t, i) => (
                        <motion.div
                            key={i}
                            variants={fadeInUp}
                            className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-xl shadow-slate-200/50 dark:shadow-none relative overflow-hidden group"
                        >
                            <Quote className="absolute -top-4 -right-4 w-24 h-24 text-blue-500/5 group-hover:text-blue-500/10 transition-colors" />
                            <p className="text-lg text-slate-700 dark:text-slate-300 mb-6 italic relative z-10">
                                "{t.quote}"
                            </p>
                            <div className="flex items-center gap-4">
                                {t.avatar ? (
                                    <img src={t.avatar} alt={t.author} className="w-12 h-12 rounded-full object-cover" />
                                ) : (
                                    <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold">
                                        {t.author[0]}
                                    </div>
                                )}
                                <div>
                                    <div className="font-bold text-slate-900 dark:text-white">{t.author}</div>
                                    <div className="text-sm text-slate-500">{t.role}</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

// --- FAQ Section ---

export const FAQSection: React.FC<{ faqs: FAQ[] }> = ({ faqs }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    return (
        <section className="py-24">
            <div className="max-w-3xl mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">Frequently Asked Questions</h2>
                    <p className="text-slate-600 dark:text-slate-400">Everything you need to know about this feature.</p>
                </div>
                <div className="space-y-4">
                    {faqs.map((faq, i) => (
                        <div
                            key={i}
                            className="border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden bg-white dark:bg-slate-900/50"
                        >
                            <button
                                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                className="w-full p-6 flex items-center justify-between text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                            >
                                <span className="font-semibold text-slate-900 dark:text-white">{faq.question}</span>
                                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${openIndex === i ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence>
                                {openIndex === i && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3, ease: "easeInOut" }}
                                    >
                                        <div className="px-6 pb-6 text-slate-600 dark:text-slate-400 leading-relaxed border-t border-slate-100 dark:border-slate-800 pt-4">
                                            {faq.answer}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

// --- CTA Section ---

export const EnhancedCTASection: React.FC<{ onGetStarted: () => void }> = ({ onGetStarted }) => {
    return (
        <section className="py-24">
            <div className="max-w-7xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[3rem] p-12 md:p-20 text-center text-white shadow-2xl shadow-blue-500/20"
                >
                    {/* Background glow */}
                    <div className="absolute top-0 right-0 w-1/2 h-full bg-white/5 blur-[100px] -rotate-12 pointer-events-none" />

                    <div className="relative z-10 max-w-3xl mx-auto">
                        <h2 className="text-4xl md:text-6xl font-bold mb-8 tracking-tight">
                            Ready to transform your <br /> visual thinking?
                        </h2>
                        <p className="text-xl text-blue-100 mb-10 leading-relaxed">
                            Join thousands of students and designers who use NoteSpace to organize their creative mind.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={onGetStarted}
                                className="w-full sm:w-auto px-10 py-5 rounded-full bg-white text-blue-600 font-bold text-xl hover:bg-blue-50 transition-all shadow-xl hover:-translate-y-1 inline-flex items-center justify-center gap-3 active:scale-95"
                            >
                                Get Started Free <ArrowRight className="w-6 h-6" />
                            </button>
                            <p className="text-sm text-blue-200/80">No credit card required. Free for individuals.</p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

// --- Step By Step Section ---

export const StepByStepSection: React.FC<{ steps: Step[], title: string }> = ({ steps, title }) => {
    return (
        <section className="py-24">
            <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-20">
                    <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6">{title}</h2>
                </div>
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={viewportConfig}
                    variants={staggerContainer}
                    className="grid md:grid-cols-3 gap-12"
                >
                    {steps.map((step, i) => (
                        <motion.div
                            key={i}
                            variants={fadeInUp}
                            className="relative"
                        >
                            {i < steps.length - 1 && (
                                <div className="hidden lg:block absolute top-12 left-full w-full h-[2px] bg-gradient-to-r from-blue-500/20 to-transparent -translate-x-12" />
                            )}
                            <div className="w-20 h-20 rounded-3xl bg-blue-600 text-white flex items-center justify-center mb-8 shadow-lg shadow-blue-500/20 text-2xl font-bold">
                                {step.icon}
                            </div>
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">{step.title}</h3>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-lg">
                                {step.description}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};
